import type { ContextItem, NamedScript } from "@/api/ai/types";
import type { AiChatSidebarProps, AttachmentKey } from "./AiChatSidebar";

function optionalField<T>(
  key: string,
  value: T | undefined | null,
): Record<string, T> {
  return value != null ? { [key]: value } : {};
}

function buildWorkspaceInfo(props: AiChatSidebarProps): ContextItem {
  const saved = props.workspaceId != null && props.workspaceId.length > 0;
  return {
    kind: "workspace_info",
    content: {
      id: props.workspaceId ?? null,
      name: props.workspaceName ?? null,
      saved,
      linked_scripts: props.linkedScripts ?? 0,
      has_schema: props.hasLinkedSchema ?? false,
      has_test_suite: props.hasLinkedTestSuite ?? false,
    },
  };
}

function inputOriginFields(props: AiChatSidebarProps) {
  if (!props.inputOrigin) return {};
  return {
    service_name: props.inputOrigin.serviceName,
    resource: props.inputOrigin.resource,
  };
}

function parseContentOrRaw(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function buildInputItem(props: AiChatSidebarProps): ContextItem | null {
  if (!props.inputDocument?.trim()) return null;
  return {
    kind: "input",
    content: parseContentOrRaw(props.inputDocument),
    ...inputOriginFields(props),
  };
}

function buildScriptsItem(
  scripts: AiChatSidebarProps["scripts"],
  attachments: Set<AttachmentKey>,
): ContextItem | null {
  if (!scripts || scripts.length === 0) return null;
  const selected: NamedScript[] = scripts
    .filter((s) => s.content.trim() && attachments.has(`script:${s.name}`))
    .map((s, i) => ({
      name: s.name || `script_${i + 1}.rhai`,
      content: s.content,
      ...(s.serviceName ? { service_name: s.serviceName } : {}),
    }));
  return selected.length > 0 ? { kind: "scripts", content: selected } : null;
}

// The last run's output — sent only with the scripts it came from.
function buildOutputItem(props: AiChatSidebarProps): ContextItem | null {
  if (!props.outputDocument?.trim()) return null;
  return { kind: "output", content: parseContentOrRaw(props.outputDocument) };
}

function schemaOriginFields(props: AiChatSidebarProps) {
  if (!props.schemaId || !props.schemaServiceName) return {};
  return { schema_id: props.schemaId, service_name: props.schemaServiceName };
}

function buildSchemaItem(props: AiChatSidebarProps): ContextItem | null {
  if (!props.schema?.trim()) return null;
  return {
    kind: "schema",
    content: parseContentOrRaw(props.schema),
    ...schemaOriginFields(props),
  };
}

function mapTestCase(tc: NonNullable<AiChatSidebarProps["testCases"]>[number]) {
  return {
    name: tc.name,
    input: tc.input,
    expected: tc.expected,
    assertions: tc.assertions,
    ...optionalField(
      "last_result",
      tc.passed !== undefined
        ? { passed: tc.passed, actual: tc.actual, error: tc.error }
        : undefined,
    ),
  };
}

function buildTestCasesItem(props: AiChatSidebarProps): ContextItem | null {
  if (!props.testCases || props.testCases.length === 0) return null;
  return {
    kind: "test_cases",
    ...optionalField("suite_id", props.testSuiteId),
    content: props.testCases.map(mapTestCase),
  };
}

function pickStage(s: {
  service_name: string;
  resource: string;
  output_encoding: string;
}) {
  return {
    service_name: s.service_name,
    resource: s.resource,
    output_encoding: s.output_encoding,
  };
}

function pipelineContent(pv: NonNullable<AiChatSidebarProps["pipelineView"]>) {
  return {
    name: pv.name || "unnamed",
    source: pv.source ? pickStage(pv.source) : null,
    middlewares: pv.middlewares.map(pickStage),
    sinks: pv.sinks.map(pickStage),
    checkpoint: pv.checkpoint,
    batch: pv.batch,
  };
}

function unresolvedField(services: AiChatSidebarProps["unresolvedServices"]) {
  const list = services ? [...services] : [];
  return list.length > 0 ? { unresolved_services: list } : {};
}

// The engine's pipeline item is a typed record, so code that does not
// parse goes as text instead: an error item naming the parse error with
// the code as written — what a "fix the pipeline" question needs.
function unparsedPipelineItem(
  pv: NonNullable<AiChatSidebarProps["pipelineView"]>,
): ContextItem | null {
  if (!pv.code?.trim()) return null;
  return {
    kind: "error",
    content: [
      `The pipeline code does not parse: ${pv.parseError}`,
      "The pipeline code as written:",
      "```json",
      pv.code,
      "```",
    ].join("\n"),
  };
}

function buildPipelineItem(props: AiChatSidebarProps): ContextItem | null {
  if (!props.pipelineView) return null;
  if (props.pipelineView.parseError) {
    return unparsedPipelineItem(props.pipelineView);
  }
  return {
    kind: "pipeline",
    content: pipelineContent(props.pipelineView),
    ...unresolvedField(props.unresolvedServices),
  };
}

// The chip-gated items, in the order the prompt expects them.
function attachedItems(
  props: AiChatSidebarProps,
  attachments: Set<AttachmentKey>,
): ContextItem[] {
  const builders: [AttachmentKey, () => ContextItem | null][] = [
    ["input", () => buildInputItem(props)],
    ["schema", () => buildSchemaItem(props)],
    ["tests", () => buildTestCasesItem(props)],
    ["pipeline", () => buildPipelineItem(props)],
  ];
  return builders
    .filter(([key]) => attachments.has(key))
    .map(([, build]) => build())
    .filter((item): item is ContextItem => item !== null);
}

export function buildContextItems(
  props: AiChatSidebarProps,
  attachments: Set<AttachmentKey>,
): ContextItem[] {
  const items: ContextItem[] = [buildWorkspaceInfo(props)];

  if (props.lastError?.trim()) {
    items.push({ kind: "error", content: props.lastError });
  }
  items.push(...attachedItems(props, attachments));

  const scriptsItem = buildScriptsItem(props.scripts, attachments);
  if (scriptsItem) items.push(scriptsItem);
  const outputItem = scriptsItem ? buildOutputItem(props) : null;
  if (outputItem) items.push(outputItem);

  return items;
}
